import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Car, CircleNotch, MagnifyingGlass, QuestionMark } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useStorefrontSiteConfig } from '@/contexts/site-config'
import {
  getStorefrontVehicleBrands,
  getStorefrontVehicleModels,
  getStorefrontVehicleYears,
  resolveStorefrontVehicleByPlate,
  type StorefrontVehicleOption,
} from '@/lib/api-shell'
import type { Category, VehicleFilter } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

type VehicleSearchMode = 'plate' | 'manual'

interface VehicleSearchPanelProps {
  categories: Category[]
  onVehicleSearch: (params: { query: string; vehicle: VehicleFilter }) => void | Promise<void>
  activeVehicleFilter?: VehicleFilter | null
  enabled?: boolean
  initialMode?: VehicleSearchMode
  variant?: 'panel' | 'dropdown' | 'inline'
  className?: string
  onSubmitComplete?: () => void
}

const normalizePlateInput = (value: string) =>
  value
    .toLocaleUpperCase('pt-BR')
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7)

const formatPlateInput = (value: string) => {
  const normalized = normalizePlateInput(value)
  return normalized.length > 3 ? `${normalized.slice(0, 3)}-${normalized.slice(3)}` : normalized
}

const extractVehicleYear = (value: string) => value.match(/\b(?:19|20)\d{2}\b/)?.[0] || value.trim()

const normalizeVehicleOptionText = (value: string) => (
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/\s+/g, ' ')
    .trim()
)

const getVehicleFilterSyncKey = (vehicle?: VehicleFilter | null) => (
  [
    vehicle?.placa?.trim() || '',
    vehicle?.marca?.trim() || '',
    vehicle?.modelo?.trim() || '',
    vehicle?.ano?.trim() || '',
    vehicle?.versao?.trim() || '',
    vehicle?.peca?.trim() || '',
  ].join('|')
)

const resolveVehicleSearchMode = (
  initialMode: VehicleSearchMode,
  activeVehicleFilter?: VehicleFilter | null,
): VehicleSearchMode => {
  if (initialMode === 'manual') return 'manual'
  if (!activeVehicleFilter) return initialMode
  if (!activeVehicleFilter.placa && (activeVehicleFilter.marca || activeVehicleFilter.modelo || activeVehicleFilter.ano)) {
    return 'manual'
  }
  return 'plate'
}

const findVehicleOptionByName = (
  options: StorefrontVehicleOption[],
  expectedName: string,
) => {
  const normalizedExpectedName = normalizeVehicleOptionText(expectedName)
  if (!normalizedExpectedName) return null

  return options.find((option) => normalizeVehicleOptionText(option.name) === normalizedExpectedName) ?? null
}

const buildVehicleSearchQuery = (partQuery: string, vehicle: VehicleFilter) =>
  [
    partQuery.trim(),
    vehicle.marca.trim(),
    vehicle.modelo.trim(),
    vehicle.ano.trim(),
    vehicle.versao?.trim() || '',
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()

const collectCategoryNames = (items: Category[]) => {
  const names = new Set<string>()
  const walk = (entries: Category[]) => {
    for (const entry of entries) {
      const name = entry.name.trim()
      if (name) names.add(name)
      if (entry.subcategories?.length) walk(entry.subcategories)
    }
  }
  walk(items)
  return Array.from(names).sort((left, right) => left.localeCompare(right, 'pt-BR', { sensitivity: 'base' }))
}

export function VehicleSearchPanel({
  categories,
  onVehicleSearch,
  activeVehicleFilter = null,
  enabled = true,
  initialMode = 'plate',
  variant = 'panel',
  className,
  onSubmitComplete,
}: VehicleSearchPanelProps) {
  const { t } = useTranslation()
  const { showVehiclePlateFilter } = useStorefrontSiteConfig()
  const [vehicleSearchMode, setVehicleSearchMode] = useState<VehicleSearchMode>(initialMode)
  const [vehiclePartQuery, setVehiclePartQuery] = useState('')
  const [isPartSuggestionsOpen, setIsPartSuggestionsOpen] = useState(false)
  const [vehiclePlateInput, setVehiclePlateInput] = useState('')
  const [vehicleBrands, setVehicleBrands] = useState<StorefrontVehicleOption[]>([])
  const [vehicleModels, setVehicleModels] = useState<StorefrontVehicleOption[]>([])
  const [vehicleYears, setVehicleYears] = useState<StorefrontVehicleOption[]>([])
  const [selectedVehicleBrandId, setSelectedVehicleBrandId] = useState('')
  const [selectedVehicleModelId, setSelectedVehicleModelId] = useState('')
  const [selectedVehicleYearId, setSelectedVehicleYearId] = useState('')
  const [isVehicleBrandsLoading, setIsVehicleBrandsLoading] = useState(false)
  const [isVehicleModelsLoading, setIsVehicleModelsLoading] = useState(false)
  const [isVehicleYearsLoading, setIsVehicleYearsLoading] = useState(false)
  const [isVehicleSearchLoading, setIsVehicleSearchLoading] = useState(false)
  const [initialManualBrandName, setInitialManualBrandName] = useState('')
  const [initialManualModelName, setInitialManualModelName] = useState('')
  const [initialManualYear, setInitialManualYear] = useState('')

  const shouldShowVehicleSearch = enabled && showVehiclePlateFilter
  const vehicleFilterSyncKey = useMemo(() => getVehicleFilterSyncKey(activeVehicleFilter), [activeVehicleFilter])
  const vehiclePartSuggestions = useMemo(() => collectCategoryNames(categories).slice(0, 80), [categories])
  const filteredVehiclePartSuggestions = useMemo(() => {
    const query = vehiclePartQuery.trim().toLocaleLowerCase('pt-BR')
    const source = query
      ? vehiclePartSuggestions.filter((name) => name.toLocaleLowerCase('pt-BR').includes(query))
      : vehiclePartSuggestions

    return source.slice(0, 18)
  }, [vehiclePartQuery, vehiclePartSuggestions])
  const selectedVehicleBrand = useMemo(
    () => vehicleBrands.find((item) => item.id === selectedVehicleBrandId) ?? null,
    [selectedVehicleBrandId, vehicleBrands]
  )
  const selectedVehicleModel = useMemo(
    () => vehicleModels.find((item) => item.id === selectedVehicleModelId) ?? null,
    [selectedVehicleModelId, vehicleModels]
  )
  const selectedVehicleYear = useMemo(
    () => vehicleYears.find((item) => item.id === selectedVehicleYearId) ?? null,
    [selectedVehicleYearId, vehicleYears]
  )
  const isDropdownVariant = variant === 'dropdown'
  const isInlineVariant = variant === 'inline'

  useEffect(() => {
    const nextMode = resolveVehicleSearchMode(initialMode, activeVehicleFilter)
    setVehicleSearchMode(nextMode)
    setVehiclePartQuery(String(activeVehicleFilter?.peca ?? '').trim())
    setVehiclePlateInput(formatPlateInput(String(activeVehicleFilter?.placa ?? '')))
    setSelectedVehicleBrandId('')
    setSelectedVehicleModelId('')
    setSelectedVehicleYearId('')
    setVehicleModels([])
    setVehicleYears([])
    setInitialManualBrandName(String(activeVehicleFilter?.marca ?? '').trim())
    setInitialManualModelName(String(activeVehicleFilter?.modelo ?? '').trim())
    setInitialManualYear(String(activeVehicleFilter?.ano ?? '').trim())
  }, [activeVehicleFilter, initialMode, vehicleFilterSyncKey])

  useEffect(() => {
    let cancelled = false

    if (!shouldShowVehicleSearch || vehicleSearchMode !== 'manual' || vehicleBrands.length > 0) {
      return () => {
        cancelled = true
      }
    }

    setIsVehicleBrandsLoading(true)
    void getStorefrontVehicleBrands({ vehicleType: 'cars' })
      .then((items) => {
        if (!cancelled) setVehicleBrands(items)
      })
      .catch(() => {
        if (!cancelled) toast.error(t('header.vehicle_search.errors.brands'))
      })
      .finally(() => {
        if (!cancelled) setIsVehicleBrandsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [shouldShowVehicleSearch, t, vehicleBrands.length, vehicleSearchMode])

  useEffect(() => {
    let cancelled = false

    if (!shouldShowVehicleSearch || vehicleSearchMode !== 'manual' || !selectedVehicleBrandId) {
      setVehicleModels([])
      return () => {
        cancelled = true
      }
    }

    setIsVehicleModelsLoading(true)
    setVehicleModels([])
    setVehicleYears([])
    void getStorefrontVehicleModels(selectedVehicleBrandId, { vehicleType: 'cars' })
      .then((items) => {
        if (!cancelled) setVehicleModels(items)
      })
      .catch(() => {
        if (!cancelled) toast.error(t('header.vehicle_search.errors.models'))
      })
      .finally(() => {
        if (!cancelled) setIsVehicleModelsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedVehicleBrandId, shouldShowVehicleSearch, t, vehicleSearchMode])

  useEffect(() => {
    let cancelled = false

    if (!shouldShowVehicleSearch || vehicleSearchMode !== 'manual' || !selectedVehicleBrandId || !selectedVehicleModelId) {
      setVehicleYears([])
      return () => {
        cancelled = true
      }
    }

    setIsVehicleYearsLoading(true)
    setVehicleYears([])
    void getStorefrontVehicleYears(selectedVehicleBrandId, selectedVehicleModelId, { vehicleType: 'cars' })
      .then((items) => {
        if (!cancelled) setVehicleYears(items)
      })
      .catch(() => {
        if (!cancelled) toast.error(t('header.vehicle_search.errors.years'))
      })
      .finally(() => {
        if (!cancelled) setIsVehicleYearsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedVehicleBrandId, selectedVehicleModelId, shouldShowVehicleSearch, t, vehicleSearchMode])

  useEffect(() => {
    if (
      !shouldShowVehicleSearch
      || vehicleSearchMode !== 'manual'
      || !initialManualBrandName
      || selectedVehicleBrandId
      || vehicleBrands.length === 0
    ) {
      return
    }

    const matchedBrand = findVehicleOptionByName(vehicleBrands, initialManualBrandName)
    if (matchedBrand) {
      setSelectedVehicleBrandId(matchedBrand.id)
    }
  }, [initialManualBrandName, selectedVehicleBrandId, shouldShowVehicleSearch, vehicleBrands, vehicleSearchMode])

  useEffect(() => {
    if (
      !shouldShowVehicleSearch
      || vehicleSearchMode !== 'manual'
      || !initialManualModelName
      || !selectedVehicleBrandId
      || selectedVehicleModelId
      || vehicleModels.length === 0
    ) {
      return
    }

    const matchedModel = findVehicleOptionByName(vehicleModels, initialManualModelName)
    if (matchedModel) {
      setSelectedVehicleModelId(matchedModel.id)
    }
  }, [
    initialManualModelName,
    selectedVehicleBrandId,
    selectedVehicleModelId,
    shouldShowVehicleSearch,
    vehicleModels,
    vehicleSearchMode,
  ])

  useEffect(() => {
    if (
      !shouldShowVehicleSearch
      || vehicleSearchMode !== 'manual'
      || !initialManualYear
      || !selectedVehicleModelId
      || selectedVehicleYearId
      || vehicleYears.length === 0
    ) {
      return
    }

    const normalizedInitialYear = normalizeVehicleOptionText(initialManualYear)
    const matchedYear = vehicleYears.find((year) => (
      extractVehicleYear(year.name) === initialManualYear
      || normalizeVehicleOptionText(year.name) === normalizedInitialYear
    ))

    if (matchedYear) {
      setSelectedVehicleYearId(matchedYear.id)
    }
  }, [
    initialManualYear,
    selectedVehicleModelId,
    selectedVehicleYearId,
    shouldShowVehicleSearch,
    vehicleSearchMode,
    vehicleYears,
  ])

  if (!shouldShowVehicleSearch) return null

  const handleVehiclePartSuggestionSelect = (name: string) => {
    setVehiclePartQuery(name)
    setIsPartSuggestionsOpen(false)
  }

  const handleVehicleSearchSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isVehicleSearchLoading) return

    try {
      setIsVehicleSearchLoading(true)
      let vehicle: VehicleFilter | null = null

      if (vehicleSearchMode === 'plate') {
        const normalizedPlate = normalizePlateInput(vehiclePlateInput)
        if (normalizedPlate.length !== 7) {
          toast.error(t('header.vehicle_search.errors.plate_required'))
          return
        }

        const resolved = await resolveStorefrontVehicleByPlate(normalizedPlate)
        vehicle = {
          placa: resolved.placa,
          marca: resolved.marca,
          modelo: resolved.modelo,
          ano: resolved.ano,
          versao: resolved.versao,
          peca: vehiclePartQuery.trim(),
        }
      } else {
        if (!selectedVehicleBrand || !selectedVehicleModel || !selectedVehicleYear) {
          toast.error(t('header.vehicle_search.errors.manual_required'))
          return
        }

        vehicle = {
          marca: selectedVehicleBrand.name,
          modelo: selectedVehicleModel.name,
          ano: extractVehicleYear(selectedVehicleYear.name),
          versao: '',
          peca: vehiclePartQuery.trim(),
        }
      }

      const query = buildVehicleSearchQuery(vehiclePartQuery, vehicle)
      if (!query) {
        toast.error(t('header.vehicle_search.errors.empty_query'))
        return
      }

      await onVehicleSearch({ query, vehicle })
      onSubmitComplete?.()
    } catch (error) {
      const message = error instanceof Error ? error.message.toLocaleLowerCase('pt-BR') : ''
      if (message.includes('vehicle_plate_invalid')) {
        toast.error(t('header.vehicle_search.errors.plate_required'))
      } else if (message.includes('vehicle_plate_not_found') || message.includes('404')) {
        toast.error(t('header.vehicle_search.errors.plate_not_found'))
      } else if (message.includes('vehicle_lookup_unavailable')) {
        setVehicleSearchMode('manual')
        toast.error(t('header.vehicle_search.errors.plate_unavailable'))
      } else {
        toast.error(t('header.vehicle_search.errors.lookup'))
      }
    } finally {
      setIsVehicleSearchLoading(false)
    }
  }

  const modeControls = isDropdownVariant ? (
    <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
      <button
        type="button"
        aria-pressed={vehicleSearchMode === 'plate'}
        onClick={() => setVehicleSearchMode('plate')}
        className={cn(
          'flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-xs font-bold text-slate-600 transition',
          vehicleSearchMode === 'plate' ? 'bg-white text-[#124f8f] shadow-sm' : 'hover:bg-white/70 hover:text-slate-900'
        )}
      >
        <Car className="h-4 w-4 shrink-0" weight="bold" />
        <span>{t('header.vehicle_search.mode.plate')}</span>
      </button>
      <button
        type="button"
        aria-pressed={vehicleSearchMode === 'manual'}
        onClick={() => setVehicleSearchMode('manual')}
        className={cn(
          'flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-xs font-bold text-slate-600 transition',
          vehicleSearchMode === 'manual' ? 'bg-white text-[#124f8f] shadow-sm' : 'hover:bg-white/70 hover:text-slate-900'
        )}
      >
        <QuestionMark className="h-4 w-4 shrink-0" weight="bold" />
        <span>{t('header.vehicle_search.mode.manual')}</span>
      </button>
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm font-semibold text-slate-900">
        <input
          type="radio"
          name="vehicle-search-mode"
          value="manual"
          checked={vehicleSearchMode === 'manual'}
          onChange={() => setVehicleSearchMode('manual')}
          className="h-5 w-5 accent-[#ff9b00]"
        />
        <span>{t('header.vehicle_search.mode.manual')}</span>
      </label>
      <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm font-semibold text-slate-900">
        <input
          type="radio"
          name="vehicle-search-mode"
          value="plate"
          checked={vehicleSearchMode === 'plate'}
          onChange={() => setVehicleSearchMode('plate')}
          className="h-5 w-5 accent-[#ff9b00]"
        />
        <span>{t('header.vehicle_search.mode.plate')}</span>
      </label>
    </div>
  )

  const inputClassName = cn(
    'w-full rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-900 outline-none transition focus:border-[#124f8f] focus:ring-2 focus:ring-[#124f8f]/20',
    isDropdownVariant ? 'h-11 px-3' : 'h-12 px-4'
  )
  const selectClassName = cn(
    inputClassName,
    'disabled:bg-slate-50 disabled:text-slate-400'
  )
  const fields = (
    <div className={cn(
      'grid gap-3',
      !isDropdownVariant && (
        vehicleSearchMode === 'manual'
          ? 'sm:grid-cols-2 xl:grid-cols-4'
          : 'sm:grid-cols-2'
      )
    )}>
      <div className="relative min-w-0">
        <label className="sr-only" htmlFor={isDropdownVariant ? 'vehicle-part-query-dropdown' : 'vehicle-part-query'}>
          {t('header.vehicle_search.part_label')}
        </label>
        <input
          id={isDropdownVariant ? 'vehicle-part-query-dropdown' : 'vehicle-part-query'}
          value={vehiclePartQuery}
          onFocus={() => setIsPartSuggestionsOpen(true)}
          onChange={(event) => {
            setVehiclePartQuery(event.target.value)
            setIsPartSuggestionsOpen(true)
          }}
          onBlur={() => {
            window.setTimeout(() => setIsPartSuggestionsOpen(false), 120)
          }}
          placeholder={t('header.vehicle_search.part_placeholder')}
          autoComplete="off"
          role="combobox"
          aria-expanded={isPartSuggestionsOpen}
          aria-controls={isDropdownVariant ? 'vehicle-part-suggestions-dropdown' : 'vehicle-part-suggestions'}
          className={inputClassName}
        />
        {isPartSuggestionsOpen && filteredVehiclePartSuggestions.length > 0 ? (
          <div
            id={isDropdownVariant ? 'vehicle-part-suggestions-dropdown' : 'vehicle-part-suggestions'}
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          >
            <div className={cn('overflow-y-auto p-1.5', isDropdownVariant ? 'max-h-52' : 'max-h-72')}>
              <div className={cn('grid gap-1', !isDropdownVariant && 'sm:grid-cols-2')}>
                {filteredVehiclePartSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleVehiclePartSuggestionSelect(name)}
                    className="rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-[#124f8f] focus:bg-blue-50 focus:text-[#124f8f] focus:outline-none"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {vehicleSearchMode === 'plate' ? (
        <div className="min-w-0">
          <label className="sr-only" htmlFor={isDropdownVariant ? 'vehicle-plate-input-dropdown' : 'vehicle-plate-input'}>
            {t('header.vehicle_search.plate_label')}
          </label>
          <input
            id={isDropdownVariant ? 'vehicle-plate-input-dropdown' : 'vehicle-plate-input'}
            value={vehiclePlateInput}
            onChange={(event) => setVehiclePlateInput(formatPlateInput(event.target.value))}
            placeholder={t('header.vehicle_search.plate_placeholder')}
            autoCapitalize="characters"
            className={cn(
              inputClassName,
              'font-bold uppercase placeholder:font-medium placeholder:normal-case'
            )}
          />
        </div>
      ) : (
        <>
          <div className="min-w-0">
            <label className="sr-only" htmlFor={isDropdownVariant ? 'vehicle-brand-select-dropdown' : 'vehicle-brand-select'}>
              {t('header.vehicle_search.brand_label')}
            </label>
            <select
              id={isDropdownVariant ? 'vehicle-brand-select-dropdown' : 'vehicle-brand-select'}
              value={selectedVehicleBrandId}
              onChange={(event) => {
                setSelectedVehicleBrandId(event.target.value)
                setSelectedVehicleModelId('')
                setSelectedVehicleYearId('')
              }}
              disabled={isVehicleBrandsLoading}
              className={cn(selectClassName, 'disabled:cursor-wait')}
            >
              <option value="">
                {isVehicleBrandsLoading
                  ? t('header.vehicle_search.loading.brands')
                  : t('header.vehicle_search.brand_placeholder')}
              </option>
              {vehicleBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <label className="sr-only" htmlFor={isDropdownVariant ? 'vehicle-model-select-dropdown' : 'vehicle-model-select'}>
              {t('header.vehicle_search.model_label')}
            </label>
            <select
              id={isDropdownVariant ? 'vehicle-model-select-dropdown' : 'vehicle-model-select'}
              value={selectedVehicleModelId}
              onChange={(event) => {
                setSelectedVehicleModelId(event.target.value)
                setSelectedVehicleYearId('')
              }}
              disabled={!selectedVehicleBrandId || isVehicleModelsLoading}
              className={cn(selectClassName, 'disabled:cursor-not-allowed')}
            >
              <option value="">
                {isVehicleModelsLoading
                  ? t('header.vehicle_search.loading.models')
                  : t('header.vehicle_search.model_placeholder')}
              </option>
              {vehicleModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <label className="sr-only" htmlFor={isDropdownVariant ? 'vehicle-year-select-dropdown' : 'vehicle-year-select'}>
              {t('header.vehicle_search.year_label')}
            </label>
            <select
              id={isDropdownVariant ? 'vehicle-year-select-dropdown' : 'vehicle-year-select'}
              value={selectedVehicleYearId}
              onChange={(event) => setSelectedVehicleYearId(event.target.value)}
              disabled={!selectedVehicleModelId || isVehicleYearsLoading}
              className={cn(selectClassName, 'disabled:cursor-not-allowed')}
            >
              <option value="">
                {isVehicleYearsLoading
                  ? t('header.vehicle_search.loading.years')
                  : t('header.vehicle_search.year_placeholder')}
              </option>
              {vehicleYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  )

  const submitButton = (
    <Button
      type="submit"
      disabled={isVehicleSearchLoading}
      className={cn(
        'gap-2 rounded-lg bg-[#124f8f] font-bold text-white hover:bg-[#0f4379] disabled:cursor-wait disabled:opacity-75',
        isDropdownVariant ? 'h-11 w-full px-4 text-sm' : 'h-12 min-w-[180px] px-7 text-base'
      )}
    >
      {isVehicleSearchLoading ? (
        <CircleNotch className="h-5 w-5 animate-spin" weight="bold" />
      ) : (
        <MagnifyingGlass className="h-5 w-5" weight="bold" />
      )}
      <span>{t('header.vehicle_search.submit')}</span>
    </Button>
  )

  return (
    <div className={cn(
      isDropdownVariant || isInlineVariant ? 'w-full' : 'container mx-auto px-4 pt-4 sm:px-6',
      className
    )}>
      <form onSubmit={handleVehicleSearchSubmit}>
        <div className={cn(
          isDropdownVariant ? 'space-y-3' : 'rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-sm'
        )}>
          <div className={cn('flex items-center gap-2 text-[#124f8f]', isDropdownVariant ? 'pb-1' : 'mb-4')}>
            <Car className="h-5 w-5 shrink-0" weight="bold" />
            <h2 className={cn('font-bold', isDropdownVariant ? 'text-sm' : 'text-base sm:text-lg')}>
              {isDropdownVariant
                ? t('header.quick_links.vehicle_search_menu.title')
                : t('header.vehicle_search.title')}
            </h2>
          </div>

          <div className={cn(
            isDropdownVariant ? 'space-y-3' : 'grid gap-3 lg:grid-cols-[320px_minmax(0,1fr)_auto] lg:items-end'
          )}>
            {modeControls}
            {fields}
            {submitButton}
          </div>
        </div>
      </form>
    </div>
  )
}
